import { AnimatePresence, motion } from 'motion/react'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useSessionSettings } from '@app/context/SessionSettingsContext'

import { useFeatureFlags } from '@context/FeatureFlagsContext'

import { ActionIcon } from '@ui/components/ActionIcon'
import { Tooltip } from '@ui/components/Tooltip'

import { Box } from '@shared/components/Box'

import { MenuLink } from '../../MenuLink'
import { themeIconMap, useThemeOptions } from '../hooks/useThemeOptions'

import styles from './TopBar.module.scss'

export const ThemeMenu: React.FC = () => {
  const [{ theme: currentTheme }] = useSessionSettings()
  const [isOpen, setIsOpen] = useState(false)
  const [themeOpen, setThemeOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const { darkMode } = useFeatureFlags()

  const { t } = useTranslation()

  const themeOptions = useThemeOptions()

  const toggle = useCallback(() => {
    setIsOpen((prev) => !prev)
    setThemeOpen(false)
  }, [])

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
        setThemeOpen(false)
      }
    }
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (themeOpen) setThemeOpen(false)
        else setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleEscape)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [themeOpen])

  const dropdownAnimation = {
    initial: { opacity: 0, y: -8 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -8 },
    transition: { duration: 0.2, ease: 'easeInOut' as const },
  }

  if (!darkMode) return null

  return (
    <Box ref={containerRef} position='relative'>
      <Box p='x4' flex align='center' justify='center'>
        <Tooltip close={isOpen} content={t('Theme.Click here to change color settings.')}>
          <ActionIcon
            icon={{ icon: themeIconMap[currentTheme], color: 'text-white' }}
            size='x24'
            onClick={toggle}
            aria-expanded={isOpen || themeOpen}
            aria-label={t('Theme.Theme')}
          />
        </Tooltip>
      </Box>

      <AnimatePresence>
        {isOpen && (
          <motion.div className={styles.profileDropdown} {...dropdownAnimation}>
            {themeOptions.map((item) => (
              <MenuLink
                key={item.id}
                section={{ ...item, screenChildren: undefined }}
                isActive={item.isSelected}
                isVisible
                isExpanded={false}
                showChevron={false}
                onClick={item?.onClick}
                className={styles.profileDropdownItem}
              />
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  )
}
